import { MatterOverViewNotesTasksEventsSidebar } from "@/components/features/matter/overview/MatterOverViewNotesTasksEventsSidebar";
import { Typography } from "@/components/ui";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { cn } from "@/lib/utils/cn";
import { useRightPanel } from "@/providers/RightPanelProvider";
import type { Matter } from "@/lib/types/Matter";
import { FileText, History, ListTodo, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

const MATTER_ACTION_BAR_HOTKEYS = "ctrl+shift+m, meta+shift+m";

type MatterActionBarPanel = "notes" | "tasks" | "eventlog";

const MATTER_ACTION_BAR_ITEMS: {
  id: MatterActionBarPanel;
  label: string;
  testId: string;
  icon: typeof FileText;
}[] = [
  {
    id: "notes",
    label: "Notes",
    testId: "notes-tab",
    icon: FileText,
  },
  {
    id: "tasks",
    label: "Tasks",
    testId: "tasks-tab",
    icon: ListTodo,
  },
  {
    id: "eventlog",
    label: "Event log",
    testId: "eventlog-tab",
    icon: History,
  },
];

export const MatterActionBar = ({ matter }: { matter: Matter }) => {
  const [activePanel, setActivePanel] = useState<MatterActionBarPanel>("notes");
  const { isPanelOpen, openPanel, closePanel, togglePanel } = useRightPanel();
  const isExpanded = isPanelOpen("matter-action-bar");

  useEffect(() => {
    return () => {
      closePanel("matter-action-bar");
    };
  }, [closePanel]);

  useHotkeys(
    MATTER_ACTION_BAR_HOTKEYS,
    (event) => {
      event.preventDefault();
      togglePanel("matter-action-bar");
    },
    {
      enableOnFormTags: true,
      preventDefault: true,
    },
    [togglePanel],
  );

  const handleActionClick = (panel: MatterActionBarPanel) => {
    if (isExpanded && activePanel === panel) {
      closePanel("matter-action-bar");
      return;
    }

    setActivePanel(panel);
    openPanel("matter-action-bar");
  };

  const activePanelLabel =
    MATTER_ACTION_BAR_ITEMS.find((item) => item.id === activePanel)?.label ??
    "Notes";

  return (
    <aside
      className={cn(
        "border-border bg-background absolute inset-y-0 right-0 z-30 flex overflow-hidden border-l transition-[width] duration-300 ease-out",
        isExpanded ? "w-[488px]" : "w-12",
      )}
      aria-label="Matter actions"
    >
      <div
        className={cn(
          "border-border bg-background h-full min-w-0 overflow-hidden border-r transition-[width,opacity] duration-300 ease-out",
          isExpanded
            ? "w-[440px] opacity-100"
            : "pointer-events-none w-0 min-w-0 opacity-0",
        )}
        aria-hidden={!isExpanded}
      >
        <div className="flex h-full w-[440px] min-w-0 flex-col">
          <div className="border-border flex shrink-0 items-center justify-between border-b px-4 py-3">
            <Typography weight="semibold" size="md">
              {activePanelLabel}
            </Typography>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground h-8 w-8"
              aria-label="Close action panel"
              onClick={() => {
                closePanel("matter-action-bar");
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <MatterOverViewNotesTasksEventsSidebar
              matter={matter}
              activeTab={activePanel}
              onTabChange={setActivePanel}
              hideTabs
            />
          </div>
        </div>
      </div>

      <nav
        aria-label="Matter action shortcuts"
        className="flex w-12 shrink-0 flex-col items-center gap-1 self-start py-3"
      >
        {MATTER_ACTION_BAR_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = isExpanded && activePanel === item.id;

          return (
            <TooltipWrapper key={item.id} message={item.label}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid={item.testId}
                aria-label={item.label}
                aria-pressed={isActive}
                className={cn(
                  "text-muted-foreground relative h-10 w-10 rounded-lg",
                  isActive && "bg-muted text-foreground",
                )}
                onClick={() => {
                  handleActionClick(item.id);
                }}
              >
                <Icon className="h-[18px] w-[18px]" />
              </Button>
            </TooltipWrapper>
          );
        })}
      </nav>
    </aside>
  );
};
